/**
 * Trigger a browser "Save As" download for a remote URL or `data:` URL.
 *
 * Cross-origin URLs are routed through `/api/download-image` so that
 * `<a download>` actually saves (browsers ignore the attribute on cross-
 * origin links and most CDNs lack permissive CORS for client `fetch`).
 *
 * Note: client-only helper, unrelated to the server-side R2 helper of the
 * same role under `@/storage`.
 */
export async function downloadFile(
  url: string,
  filename?: string
): Promise<void> {
  const isDataUrl = url.startsWith('data:');
  const finalName = filename ?? defaultFilename(url);

  let href = url;

  if (!isDataUrl && isCrossOrigin(url)) {
    const params = new URLSearchParams({ url, filename: finalName });
    href = `/api/download-image?${params.toString()}`;
  }

  const anchor = document.createElement('a');
  anchor.href = href;
  anchor.download = finalName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
}

function isCrossOrigin(url: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const parsed = new URL(url, window.location.href);
    return parsed.origin !== window.location.origin;
  } catch {
    return false;
  }
}

function defaultFilename(url: string): string {
  const stamp = Date.now();
  if (url.startsWith('data:')) {
    const mime = url.slice(5, url.indexOf(';'));
    const ext = mime.split('/')[1]?.split('+')[0] ?? 'bin';
    return `ai-image-${stamp}.${ext}`;
  }
  try {
    const path = new URL(url).pathname;
    const last = path.split('/').pop() ?? '';
    if (last && /\.[a-z0-9]{2,5}$/i.test(last)) return last;
  } catch {
    /* ignore */
  }
  return `ai-image-${stamp}.jpg`;
}
